import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useHistory } from 'react-router'
import { useCookies } from 'react-cookie'
import TagManager from 'react-gtm-module'

import './CreateOrganization.scss'

import Credential from '../../components/Credential'
import DropDownField from 'components/DropDownField'
import useGetCurrencies from 'containers/currencies/useGetCurrencies'
import { guessCurrencyFromLocale } from 'utils/localeCurrency'

import useCreateOrganization from '../../containers/organizations/useCreateOrganization'
import useGetOrganizations from '../../containers/organizations/useGetOrganizations'
import ScreenLoading from '../../components/ScreenLoading'
import { getStoredShopifyParams, buildShopifyQueryString, consumeShopifyResumePending } from 'utils/shopifyParams'

import mayaLogo from 'images/maya-logo.png'
import usePageTitle from 'containers/pageTitle/usePageTitle'

const CreateOrganization = () => {
  const {
    createOrganization,
    createOrganizationError,
    removeCreateOrganizationError,
    isCreatingOrganization,
  } = useCreateOrganization()
  const [organizationName, setOrganizationName] = useState('')
  // the reporting currency: required, pre-filled from the browser locale, options = every
  // currency the exchange-rate feed quotes (GET /currencies)
  const [currency, setCurrency] = useState('')
  const [currencies, fetchCurrencies] = useGetCurrencies()
  const { clearOrganizationsCache } = useGetOrganizations()
  const { organizations, getOrganizations, loadingOrganizations } =
    useGetOrganizations()
  let history = useHistory()
  const [, , removeCookie] = useCookies(['user_id'])

  //Page title
  usePageTitle('Create Organization - Maya')

  const getData = async () => {
    await getOrganizations()
  }

  useEffect(() => {
    getData()
    fetchCurrencies().then((list) => {
      if (list && list.length) {
        setCurrency(guessCurrencyFromLocale(list.map((c) => c.code)))
      }
    })
    // eslint-disable-next-line
  }, [])

  if (loadingOrganizations) {
    return <ScreenLoading />
  }

  const handleSubmit = async () => {
    const createdOrganization = await createOrganization(organizationName, currency)
    if (createdOrganization) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'created_organization',
          is_custom_track: true,
          event_params: {
            organizationId: createdOrganization.data.id,
            organizationSlug: createdOrganization.data.slug,
            organizationName: createdOrganization.data.name,
          }
        },
      })
      // Clear cache and refetch organizations to ensure new org is loaded
      await clearOrganizationsCache()
      await getOrganizations()
      // If this org was created mid Shopify install, resume that flow instead of
      // dropping the merchant on /sources (which abandoned the connect — they'd just
      // see "created an org and nothing else"). Resume ONLY on the explicit marker set
      // by ShopifyIntegration's "no orgs yet" hop — stored params alone must not
      // hijack an unrelated org creation into a Shopify connect (#1645: 24h-lived
      // params rerouted every new org to the connect flow, and each hijacked
      // authorize invalidated the store's previous working grant).
      const shopifyParams = consumeShopifyResumePending() ? getStoredShopifyParams() : null
      if (shopifyParams) {
        history.push(`/shopify-integration?${buildShopifyQueryString(shopifyParams)}`)
      } else {
        // Redirect to sources page for the newly created organization
        history.push(`/organization/${createdOrganization.data.slug}/sources`)
      }
    }
  }

  const handleCancel = () => {
    if (organizations.length > 0) {
      history.push({
      pathname: '/',
    })
    } else {
      removeCookie('user_id', { path: '/' })
      history.push({
      pathname: '/',
      state: {
        destinations: true,
      },
    })
    }
  }

  return (
    <div className="page-column">
      <header>
        <div className="logo">
          <img src={mayaLogo} alt="Maya" />
        </div>
      </header>
      <div className="container-grow">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="container-column create-organization-box"
        >
          <div>
            <h1 className="text-align-center">Create organization</h1>
            <div className="secondary-text text-align-center">
              You can rename it or join another organization later
            </div>
          </div>
          <div className="form-tip">
            <h3>Tip</h3>
            <p>
              If your company has a lot of brands or websites or in different
              countries it will be good to add every entity in a different
              organization. ex. If you are Google add as a different
              organization Gmail & Google search. In the end you want different
              products in a different organization so you can extract value.
            </p>
          </div>
          <div className="container-center container-column container-grow create-organization">
            <Credential
              value={organizationName}
              setValue={setOrganizationName}
              label="Name"
              type="text"
              spellcheck="false"
              error={createOrganizationError}
              removeError={() => removeCreateOrganizationError()}
            />
            <DropDownField
              label="Reporting currency"
              options={(currencies || []).map((c) => ({ label: `${c.code} — ${c.name}`, value: c.code }))}
              value={currency}
              onChange={(value) => { removeCreateOrganizationError(); setCurrency(value) }}
              loadingOptions={!currencies || currencies.length === 0}
              searchable
              required
              helperText="Every monetary value is converted into this currency. Admins can change it later in Settings → Organization."
              style={{ width: '100%', marginTop: 12 }}
            />
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              {!isCreatingOrganization ? (
                <button
                  disabled={isCreatingOrganization || !currency}
                  onClick={handleSubmit}
                  className="submit-button"
                >
                  Continue
                </button>
              ) : (
                <ScreenLoading />
              )}
            </div>
            <button onClick={handleCancel} className="link">
              Cancel
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default React.memo(CreateOrganization)
